import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CurrencyProvider } from './contexts/CurrencyContext';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { Layout } from './components/layout/Layout';

// Auth Pages
import { Login } from './pages/auth/Login';
import { Register } from './pages/auth/Register';
import { ForgotPassword } from './pages/auth/ForgotPassword';
import { ResetPassword } from './pages/auth/ResetPassword';

// Dashboard Pages
import { Dashboard } from './pages/dashboard/Dashboard';

// Invoice Pages
import { InvoiceList } from './pages/invoices/InvoiceList';
import { InvoiceCreate } from './pages/invoices/InvoiceCreate';
import { InvoiceCreateFromQuote } from './pages/invoices/InvoiceCreateFromQuote';
import { InvoiceEdit } from './pages/invoices/InvoiceEdit';
import { InvoiceView } from './pages/invoices/InvoiceView';

// Quote Pages
import { QuoteList } from './pages/quotes/QuoteList';
import { QuoteCreate } from './pages/quotes/QuoteCreate';
import { QuoteEdit } from './pages/quotes/QuoteEdit';
import { QuoteView } from './pages/quotes/QuoteView';

// Client Pages
import { ClientList } from './pages/clients/ClientList';
import { ClientCreate } from './pages/clients/ClientCreate';
import { ClientEdit } from './pages/clients/ClientEdit';
import { ClientView } from './pages/clients/ClientView';

// Product Pages
import { ProductList } from './pages/products/ProductList';
import { ProductCreate } from './pages/products/ProductCreate';
import { ProductEdit } from './pages/products/ProductEdit';

// Settings Pages
import { Settings } from './pages/settings/Settings';
import { Profile } from './pages/settings/Profile';
import { Subscription } from './pages/settings/Subscription';

// Client Portal Pages
import { ClientPortal } from './pages/client/ClientPortal';
import { ClientInvoices } from './pages/client/ClientInvoices';
import { ClientQuotes } from './pages/client/ClientQuotes';

import styles from './styles/global.module.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function AppRoutes() {
  const { user } = useAuth();

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={!user ? <Login /> : <Navigate to="/dashboard" />} />
      <Route path="/register" element={!user ? <Register /> : <Navigate to="/dashboard" />} />
      <Route path="/forgot-password" element={!user ? <ForgotPassword /> : <Navigate to="/dashboard" />} />
      <Route path="/reset-password" element={!user ? <ResetPassword /> : <Navigate to="/dashboard" />} />

      {/* Client Portal Routes */}
      <Route path="/portal" element={<ClientPortal />} />
      <Route path="/portal/invoices" element={<ClientInvoices />} />
      <Route path="/portal/quotes" element={<ClientQuotes />} />

      {/* Protected Routes */}
      <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<Navigate to="/dashboard" />} />
        <Route path="dashboard" element={<Dashboard />} />
        
        {/* Invoice Routes */}
        <Route path="invoices" element={<InvoiceList />} />
        <Route path="invoices/create" element={<InvoiceCreate />} />
        <Route path="invoices/create-from-quote" element={<InvoiceCreateFromQuote />} />
        <Route path="invoices/:id" element={<InvoiceView />} />
        <Route path="invoices/:id/edit" element={<InvoiceEdit />} />
        
        {/* Quote Routes */}
        <Route path="quotes" element={<QuoteList />} />
        <Route path="quotes/create" element={<QuoteCreate />} />
        <Route path="quotes/:id" element={<QuoteView />} />
        <Route path="quotes/:id/edit" element={<QuoteEdit />} />
        
        {/* Client Routes */}
        <Route path="clients" element={<ClientList />} />
        <Route path="clients/create" element={<ClientCreate />} />
        <Route path="clients/:id" element={<ClientView />} />
        <Route path="clients/:id/edit" element={<ClientEdit />} />
        
        {/* Product Routes */}
        <Route path="products" element={<ProductList />} />
        <Route path="products/create" element={<ProductCreate />} />
        <Route path="products/:id/edit" element={<ProductEdit />} />
        
        {/* Settings Routes */}
        <Route path="settings" element={<Settings />} />
        <Route path="settings/profile" element={<Profile />} />
        <Route path="settings/subscription" element={<Subscription />} />
      </Route>

      {/* Catch all route */}
      <Route path="*" element={<Navigate to="/dashboard" />} />
    </Routes>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <CurrencyProvider>
          <Router>
            <div className={styles.minHFull}>
              <AppRoutes />
              <ToastContainer
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
              />
            </div>
          </Router>
        </CurrencyProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
